/**
 * DmTemplatesSettings — Edytowalne szablony Auto DM
 * Admin/user can view and edit their DM templates for Auto DM system
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  MessageCircle, Save, RotateCcw, ArrowLeft, CheckCircle2,
  AlertCircle, Loader2, Mail, MessageSquare
} from 'lucide-react';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';

const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const TEMPLATE_FIELDS = [
  { key: 'dm_lead', icon: Mail, labelKey: 'dmTemplates.dmLead', descKey: 'dmTemplates.dmLeadDesc' },
  { key: 'dm_question', icon: MessageSquare, labelKey: 'dmTemplates.dmQuestion', descKey: 'dmTemplates.dmQuestionDesc' },
  { key: 'reply_lead', icon: MessageCircle, labelKey: 'dmTemplates.replyLead', descKey: 'dmTemplates.replyLeadDesc' },
  { key: 'reply_question', icon: MessageCircle, labelKey: 'dmTemplates.replyQuestion', descKey: 'dmTemplates.replyQuestionDesc' },
  { key: 'reply_warm', icon: MessageCircle, labelKey: 'dmTemplates.replyWarm', descKey: 'dmTemplates.replyWarmDesc' },
];

export default function DmTemplatesSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [templates, setTemplates] = useState({});
  const [originalTemplates, setOriginalTemplates] = useState({});
  const [isCustom, setIsCustom] = useState(false);
  const [businessType, setBusinessType] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/dm-templates`, { headers: headers() });
      setTemplates(data.templates);
      setOriginalTemplates(data.templates);
      setIsCustom(data.is_custom);
      setBusinessType(data.business_type);
      setUpdatedAt(data.updated_at);
    } catch {
      showToast('Błąd ładowania szablonów', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data } = await axios.put(`${API}/dm-templates`, templates, { headers: headers() });
      setUpdatedAt(data.updated_at);
      setIsCustom(true);
      setOriginalTemplates({ ...templates });
      showToast(t('dmTemplates.templatesSaved'), 'success');
    } catch {
      showToast(t('dmTemplates.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setResetting(true);
      await axios.post(`${API}/dm-templates/reset`, {}, { headers: headers() });
      await fetchTemplates();
      showToast(t('dmTemplates.resetDone'), 'success');
    } catch {
      showToast(t('dmTemplates.resetError'), 'error');
    } finally {
      setResetting(false);
    }
  };

  const showToast = (msg, type) => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const hasChanges = JSON.stringify(templates) !== JSON.stringify(originalTemplates);

  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center min-h-[400px]" data-testid="dm-templates-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[#F47A3B]" />
        </div>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="dm-templates-page">
        {/* Toast */}
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium ${
              toast.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
            data-testid="dm-templates-toast"
          >
            {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {toast.msg}
          </motion.div>
        )}

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-zinc-100 transition-colors"
            data-testid="dm-templates-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900" data-testid="dm-templates-title">
              Szablony Auto DM
            </h1>
            <p className="text-sm text-zinc-500">
              Edytuj treści automatycznych wiadomości DM i odpowiedzi publicznych
            </p>
          </div>
        </div>

        {/* Status info */}
        <div className="flex items-center gap-4 mb-6 text-xs text-zinc-500">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${
            isCustom ? 'bg-[#FFF3EB] text-[#F47A3B]' : 'bg-zinc-100 text-zinc-600'
          }`} data-testid="dm-templates-status">
            {isCustom ? t('dmTemplates.customTemplates') : t('dmTemplates.defaultTemplates')}
          </span>
          <span>{t('dmTemplates.type')}: {businessType === 'hodowla' ? t('dmTemplates.breeding') : t('dmTemplates.company')}</span>
          {updatedAt && (
            <span>Ostatnia zmiana: {new Date(updatedAt).toLocaleDateString('pl-PL')}</span>
          )}
        </div>

        {/* Template fields */}
        <div className="space-y-5">
          {TEMPLATE_FIELDS.map((field) => {
            const Icon = field.icon;
            return (
              <motion.div
                key={field.key}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm"
                data-testid={`dm-template-field-${field.key}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-[#F47A3B]" />
                  <label className="text-sm font-semibold text-zinc-800">
                    {t(field.labelKey)}
                  </label>
                </div>
                <p className="text-xs text-zinc-400 mb-3">{t(field.descKey)}</p>
                <textarea
                  value={templates[field.key] || ''}
                  onChange={(e) => setTemplates(prev => ({ ...prev, [field.key]: e.target.value }))}
                  className="w-full min-h-[100px] p-3 rounded-xl border border-zinc-200 bg-zinc-50 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#F47A3B]/30 focus:border-[#F47A3B] resize-y transition-all"
                  placeholder={t('dmTemplates.placeholder')}
                  data-testid={`dm-template-input-${field.key}`}
                />
                <div className="flex justify-end mt-1">
                  <span className="text-[10px] text-zinc-400">
                    {(templates[field.key] || '').length} {t('dmTemplates.chars')}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-zinc-100">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={resetting || !isCustom}
            className="gap-2 text-zinc-600"
            data-testid="dm-templates-reset-btn"
          >
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            {t('dmTemplates.resetDefaults')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="gap-2 bg-[#F47A3B] hover:bg-[#E06A2B] text-white"
            data-testid="dm-templates-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('dmTemplates.saveTemplates')}
          </Button>
        </div>
      </div>
    </PanelLayout>
  );
}
